import * as types from '../types';
import { IEstimatesApi } from '../contracts';
import { HttpApi } from './HttpApi';

const enc = encodeURIComponent;

/** Only the filters that are set go on the query string (the server rejects blanks). */
function params(query: Record<string, string | number | undefined> = {}): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') result[key] = value;
  });
  return result;
}

const decisionPath = (decision: types.EstimateDecision) => (decision === 'APPROVED' ? 'approve' : 'decline');

/** Estimates / quotes (tracker item 39). */
export class EstimatesHttpApi extends HttpApi implements IEstimatesApi {
  async listEstimates(query: types.EstimateListQuery = {}): Promise<types.EstimatePage<types.EstimateSummary>> {
    return (await this.http.get<types.APIResponse<types.EstimatePage<types.EstimateSummary>>>('/estimates', { params: params({ ...query }) })).data.data;
  }

  async getEstimate(id: string): Promise<types.Estimate> {
    return (await this.http.get<types.APIResponse<types.Estimate>>(`/estimates/${enc(id)}`)).data.data;
  }

  async previewEstimate(data: types.EstimateInput): Promise<types.EstimatePreview> {
    return (await this.http.post<types.APIResponse<types.EstimatePreview>>('/estimates/preview', data)).data.data;
  }

  async createEstimate(data: types.EstimateInput): Promise<types.Estimate> {
    return (await this.http.post<types.APIResponse<types.Estimate>>('/estimates', data)).data.data;
  }

  async updateEstimate(id: string, data: types.EstimateUpdateInput): Promise<types.Estimate> {
    return (await this.http.patch<types.APIResponse<types.Estimate>>(`/estimates/${enc(id)}`, data)).data.data;
  }

  async sendEstimate(id: string, data: types.EstimateSendInput = {}): Promise<types.EstimateSendResult> {
    return (await this.http.post<types.APIResponse<types.EstimateSendResult>>(`/estimates/${enc(id)}/send`, data)).data.data;
  }

  async recordEstimateResponse(id: string, decision: types.EstimateDecision, data: types.EstimateResponseInput = {}): Promise<types.Estimate> {
    return (await this.http.post<types.APIResponse<types.Estimate>>(`/estimates/${enc(id)}/${decisionPath(decision)}`, data)).data.data;
  }

  async cancelEstimate(id: string, reason?: string | null): Promise<types.Estimate> {
    return (await this.http.post<types.APIResponse<types.Estimate>>(`/estimates/${enc(id)}/cancel`, reason ? { reason } : {})).data.data;
  }

  async convertEstimate(id: string, data: types.EstimateConvertInput): Promise<types.EstimateConversionResult> {
    return (await this.http.post<types.APIResponse<types.EstimateConversionResult>>(`/estimates/${enc(id)}/convert`, data)).data.data;
  }

  async addEstimateBundle(id: string, data: { bundleId: string; startDate?: string | null }): Promise<types.EstimateBundleAddResult> {
    return (await this.http.post<types.APIResponse<types.EstimateBundleAddResult>>(`/estimates/${enc(id)}/bundles`, data)).data.data;
  }

  async updateEstimateBundle(id: string, bundleId: string, data: { startDate: string | null }): Promise<types.Estimate> {
    return (await this.http.patch<types.APIResponse<types.Estimate>>(`/estimates/${enc(id)}/bundles/${enc(bundleId)}`, data)).data.data;
  }

  async removeEstimateBundle(id: string, bundleId: string): Promise<types.Estimate> {
    return (await this.http.delete<types.APIResponse<types.Estimate>>(`/estimates/${enc(id)}/bundles/${enc(bundleId)}`)).data.data;
  }

  async listPortalEstimates(query: { page?: number; limit?: number } = {}): Promise<types.EstimatePage<types.PortalEstimate>> {
    return (await this.http.get<types.APIResponse<types.EstimatePage<types.PortalEstimate>>>('/portal/estimates', { params: params({ ...query }) })).data.data;
  }

  async getPortalEstimate(id: string): Promise<types.PortalEstimate> {
    return (await this.http.get<types.APIResponse<types.PortalEstimate>>(`/portal/estimates/${enc(id)}`)).data.data;
  }

  async respondPortalEstimate(id: string, decision: types.EstimateDecision, comment?: string | null): Promise<types.PortalEstimate> {
    const body = comment ? { comment } : {};
    return (await this.http.post<types.APIResponse<types.PortalEstimate>>(`/portal/estimates/${enc(id)}/${decisionPath(decision)}`, body)).data.data;
  }

  async getPublicEstimate(token: string): Promise<types.PublicEstimateView> {
    return (await this.http.get<types.APIResponse<types.PublicEstimateView>>(`/public/estimates/${enc(token)}`)).data.data;
  }

  async respondPublicEstimate(token: string, decision: types.EstimateDecision, data: types.EstimateResponseInput): Promise<types.PublicEstimateView> {
    return (await this.http.post<types.APIResponse<types.PublicEstimateView>>(`/public/estimates/${enc(token)}/${decisionPath(decision)}`, data)).data.data;
  }
}
